import React, { useState } from "react";
import PropTypes from "prop-types";
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  Menu,
  MenuItem,
  IconButton,
  Typography,
  Toolbar,
  Box,
  useMediaQuery,
} from "@mui/material";
import { useTheme } from "@mui/material/styles";
import TableSortLabel from "@mui/material/TableSortLabel";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import { useStyles } from "../../assets/styles/Theme";
import { Tooltip } from "@mui/material";
function ViewData({
  columns,
  rows,
  menuItems,
  tabletitle,
  gridid,
  displayActions = true,
}) {
  const [order, setOrder] = useState("asc");
  const [orderBy, setOrderBy] = useState("calories");
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [anchorEl, setAnchorEl] = useState(null);
  const theme = useTheme();
  const styles = useStyles(theme);
  const isMediumScreen = useMediaQuery(theme.breakpoints.down("md"));

  const handleRequestSort = (event, property) => {
    const isAsc = orderBy === property && order === "asc";
    setOrder(isAsc ? "desc" : "asc");
    setOrderBy(property);
  };

  const handleChangePage = (event, newPage) => setPage(newPage);

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const visibleRows = React.useMemo(
    () =>
      stableSort(rows, getComparator(order, orderBy)).slice(
        page * rowsPerPage,
        page * rowsPerPage + rowsPerPage
      ),
    [order, orderBy, page, rowsPerPage, rows]
  );

  const handleOpenUserMenu = (event, row) => {
    setAnchorEl({ anchor: event.currentTarget, row });
  };

  const handleCloseUserMenu = () => {
    setAnchorEl(null);
  };

  const handleMenuItemClick = (menuItem, row) => {
    menuItem.onClick(row[gridid]);
    handleCloseUserMenu();
  };

  if (!columns || !rows || !Array.isArray(columns) || !Array.isArray(rows)) {
    console.error("Invalid columns or rows");
    return null;
  }

  // Filter columns for medium screens
  const filteredColumns = isMediumScreen ? columns.slice(0, 4) : columns;

  return (
    <div>
      <Box sx={styles.AddStyledBox}>
        <Toolbar
          sx={{
            maxWidth: "100%",
            pl: { sm: 2 },
            pr: { xs: 1, sm: 1 },
            background: theme.palette.background.paper,
            marginTop: {
              xs: 18,
              md: "135px",
            },
          }}
        >
          <Typography
            variant="h6"
            id="tableTitle"
            component="div"
            sx={{
              ...theme.typography.h2,
              color: theme.palette.text.primary,
              flex: "1 1 100%",
              ml: 1,
            }}
          >
            {tabletitle}
          </Typography>
        </Toolbar>
        <TableContainer sx={styles.VI_ViewStyledPaper}>
          <Table stickyHeader>
            <TableHead>
              <TableRow sx={{ background: theme.palette.background.paper }}>
                {filteredColumns.map((column) => (
                  <TableCell
                    key={column.id}
                    sortDirection={orderBy === column.id ? order : false}
                    sx={{
                      ...styles.AI_headtableCell,
                      textAlign: column.textAlign,
                      textTransform: "none",
                    }}
                  >
                    {column.disableSort ? (
                      getColumnHeaderLabel(column)
                    ) : (
                      <TableSortLabel
                        active={orderBy === column.id}
                        direction={orderBy === column.id ? order : "asc"}
                        onClick={(event) => handleRequestSort(event, column.id)}
                        hideSortIcon={column.textAlign === "center"}
                        sx={{
                          flexDirection:
                            column.textAlign === "right"
                              ? "row-reverse"
                              : "row",
                          justifyContent:
                            column.textAlign === "right"
                              ? "flex-end"
                              : "flex-start",
                        }}
                      >
                        {getColumnHeaderLabel(column)}
                      </TableSortLabel>
                    )}
                  </TableCell>
                ))}
                {displayActions ? (
                  <TableCell
                    sx={{
                      ...styles.AI_headtableCell,
                      textAlign: "center",
                      position: "sticky",
                      right: 0,
                      background: theme.palette.background.tertiary,
                      boxShadow: `0px 0px 2px 0px ${theme.palette.input_field.stroke}`,
                      zIndex: theme.zIndex.appBar, // Ensure it stays on top
                      maxWidth: "150px", // Set a maximum width for the actions cell
                      minWidth: "100px", // Set a minimum width for the actions cell
                    }}
                  >
                    Actions
                  </TableCell>
                ) : (
                  <></>
                )}
              </TableRow>
            </TableHead>
            <TableBody>
              {visibleRows.length === 0 ? (
                <TableRow>
                  <TableCell
                    colSpan={filteredColumns.length + (displayActions ? 1 : 0)}
                    align="center"
                  >
                    No Data Found
                  </TableCell>
                </TableRow>
              ) : (
                <></>
              )}

              {visibleRows.map((row) => (
                <TableRow hover key={row[gridid]}>
                  {filteredColumns.map((column) => {
                    const cellAlign = column.textAlign || "left";
                    const tooltipPlacement =
                      cellAlign === "right"
                        ? "top-end"
                        : cellAlign === "center"
                          ? "top"
                          : "top-start";
                    return (
                      <TableCell
                        key={column.id}
                        sx={{
                          ...styles.AI_tablecell,
                          padding: displayActions ? 1 : 2,
                          textAlign: cellAlign,
                          backgroundColor:
                            column.id === "COLORCODE" ? row.COLORCODE : "",
                          maxWidth: "100px",
                        }}
                      >
                        <Tooltip
                          title={
                            column.disableTooltip
                              ? ""
                              : row[column.tooltipField || column.id] || ""
                          }
                          arrow
                          placement={tooltipPlacement}
                          disableHoverListener={!!column.disableTooltip}
                          disableFocusListener={!!column.disableTooltip}
                          disableTouchListener={!!column.disableTooltip}
                          componentsProps={{
                            tooltip: {
                              sx: { textAlign: cellAlign },
                            },
                          }}
                        >
                          <Box
                            sx={{
                              overflow: "hidden",
                              textOverflow: "ellipsis",
                              whiteSpace: "nowrap",
                              width: "100%",
                              display: "block",
                              textAlign: cellAlign,
                              cursor: "pointer",
                            }}
                          >
                            {row[column.id]}
                          </Box>
                        </Tooltip>
                      </TableCell>
                    );
                  })}
                  {displayActions ? (
                    <TableCell
                      sx={{
                        ...styles.AI_tablecellX,
                        position: "sticky",
                        right: 0,
                        background: theme.palette.background.fill,
                        zIndex: 1,
                      }}
                    >
                      {(() => {
                        const resolvedMenuItems =
                          typeof menuItems === "function"
                            ? menuItems(row)
                            : menuItems;
                        if (!Array.isArray(resolvedMenuItems) || resolvedMenuItems.length === 0) {
                          return null;
                        }
                        return (
                          <IconButton
                            onClick={(event) =>
                              handleOpenUserMenu(event, {
                                ...row,
                                __resolvedMenuItems: resolvedMenuItems,
                              })
                            }
                          >
                            <MoreVertIcon />
                          </IconButton>
                        );
                      })()}
                      <Menu
                        anchorEl={anchorEl?.anchor}
                        open={Boolean(anchorEl)}
                        onClose={handleCloseUserMenu}
                        sx={{ mt: 0, ml: 3 }}
                      >
                        {(anchorEl?.row?.__resolvedMenuItems ||
                          (typeof menuItems === "function"
                            ? menuItems(anchorEl?.row)
                            : menuItems) ||
                          []
                        ).map((menuItem, index) => (
                          <MenuItem
                            key={index}
                            onClick={() =>
                              handleMenuItemClick(menuItem, anchorEl?.row)
                            }
                            sx={{
                              display: "flex",
                              gap: 1,
                              "& .MuiSvgIcon-root": {
                                color: theme.palette.primary.main,
                              },
                              "& .menuItemText": {
                                color: theme.palette.primary.main,
                                fontSize: "16px",
                              },
                            }}
                          >
                            {menuItem.icon}
                            <span className="menuItemText">
                              {menuItem.text}
                            </span>
                          </MenuItem>
                        ))}
                      </Menu>
                    </TableCell>
                  ) : (
                    <></>
                  )}
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
        <TablePagination
          rowsPerPageOptions={[5, 10, 50, 100, 500, 1000]}
          component="div"
          count={rows.length}
          rowsPerPage={rowsPerPage}
          page={page}
          onPageChange={handleChangePage}
          onRowsPerPageChange={handleChangeRowsPerPage}
        />
      </Box>
    </div>
  );
}

ViewData.propTypes = {
  columns: PropTypes.array.isRequired,
  rows: PropTypes.array.isRequired,
  menuItems: PropTypes.arrayOf(
    PropTypes.shape({
      text: PropTypes.string.isRequired,
      onClick: PropTypes.func.isRequired,
      icon: PropTypes.node, // Add icon to PropTypes
    })
  ).isRequired,
  tabletitle: PropTypes.string.isRequired,
  gridid: PropTypes.string.isRequired,
};

ViewData.defaultProps = {
  columns: [],
  rows: [],
  menuItems: [],
  tabletitle: "",
  gridid: "",
};

/**
 * Default: title-case each word from Griddata labels (e.g. "ORDER CODE" → "Order Code").
 * Override: column.discardformatting + exact label text for acronyms (e.g. "CN / EX Number").
 */
function titleCaseColumnLabel(label) {
  if (label == null || label === "") return "";
  return String(label)
    .toLowerCase()
    .replace(/\b\w/g, (c) => c.toUpperCase());
}

function getColumnHeaderLabel(column) {
  if (column?.discardformatting) return column.label ?? "";
  return titleCaseColumnLabel(column?.label);
}

// Comparator functions for sorting
function descendingComparator(a, b, orderBy) {
  if (b[orderBy] < a[orderBy]) return -1;
  if (b[orderBy] > a[orderBy]) return 1;
  return 0;
}

function getComparator(order, orderBy) {
  return order === "desc"
    ? (a, b) => descendingComparator(a, b, orderBy)
    : (a, b) => -descendingComparator(a, b, orderBy);
}

function stableSort(array, comparator) {
  const stabilizedThis = array.map((el, index) => [el, index]);
  stabilizedThis.sort((a, b) => {
    const order = comparator(a[0], b[0]);
    if (order !== 0) return order;
    return a[1] - b[1];
  });
  return stabilizedThis.map((el) => el[0]);
}

export default ViewData;
